import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useRouter } from 'vue-router'
import { MY_ROUTES } from '@/router'

import { getOrder, getOrders, resendOrderEmail as apiResendOrderEmail } from '@/services/api/orders'
import { getError } from '@/utils/errors'

export const enum PAYMENT_METHOD {
  OTHER_PAYMENT = 'Other Payment',
  APPLE_WALLET = 'Apple Wallet',
  GOOGLE_WALLET = 'Google Wallet'
}

export const useOrderStore = defineStore('order', () => {
  const applePassUrl = ref('')
  const customerId = ref('')
  const customerEmail = ref('')
  const customerName = ref('')
  const customerPhone = ref('')
  const customerAddressLine1 = ref('')
  const customerAddressLine2 = ref('')
  const customerCity = ref('')
  const customerCountry = ref('')
  const customerPostalCode = ref('')
  const customerState = ref('')
  const customerWalletEmail = ref('')
  const googlePassUrl = ref('')
  const id = ref('')
  const isProcessing = ref(false)
  const referenceId = ref('')
  const paymentMethod = ref<PAYMENT_METHOD>()
  const createIndividualPasses = ref(false)
  const router = useRouter()
  const hasPrimaryCard = ref(false)
  const items = ref<OrderItem[]>([])
  const wallet = ref<PAYMENT_METHOD>()
  const status = ref('')
  const refunds = ref<OrderRefund[]>([])
  const total = computed<number>(() => {
    return items.value.reduce((total, item) => total + item.quantity * item.price, 0)
  })

  const totalInCents = computed<number>(() => {
    return Math.floor(total.value * 100)
  })

  const passesQuantity = computed<number>(() => {
    return items.value.reduce((total, item) => total + item.quantity, 0)
  })

  function isLoaded(): boolean {
    return !!id.value
  }

  async function fetchOrder(orderId: string) {
    if (id.value === orderId) return

    const details = await getOrder(orderId)
    if (!details.error) {
      updateFromResponse(details)
    } else {
      router.push({ name: MY_ROUTES.NOT_FOUND })
    }
  }

  async function fetchOrders(
    page: ApiPage = 1,
    filters: ApiFilter[] = []
  ): Promise<PaginationResponse & { orders: Order[] }> {
    const response = await getOrders(page, filters)

    if (!response.error) {
      return {
        currentPage: page,
        totalPages: response.total_pages,
        totalCount: response.total_count,
        orders: response.data.map((orderResponse) => ({
          id: orderResponse.id,
          customerEmail: orderResponse.customer_email,
          associatedEmail: orderResponse.associated_email,
          description: orderResponse.description,
          dateOfPurchase: orderResponse.date_of_purchase,
          orderNo: orderResponse.reference_id,
          appleUrl: orderResponse.apple_pass_url,
          googleUrl: orderResponse.google_pass_url,
          paymentMethodType: orderResponse.payment_method_type,
          paymentMethodDetails: orderResponse.payment_method_details,
          subtotal: orderResponse.subtotal,
          coupon: orderResponse.coupon,
          total: orderResponse.total,
          items: orderResponse.items,
          payments: orderResponse.payments,
          primaryActions: orderResponse.primary_actions,
          secondaryActions: orderResponse.secondary_actions,
          status: orderResponse.status,
          refunds: orderResponse.refunds
        }))
      }
    } else {
      return { error: getError(response), orders: [] }
    }
  }

  async function resendOrderEmail(orderId: string, email: string): Promise<ApiNoContentResponse> {
    return await apiResendOrderEmail(orderId, email)
  }

  function updateFromResponse(response: CreateOrderResponse) {
    if (response.id) id.value = response.id
    if (response.reference_id) referenceId.value = response.reference_id
    if (response.customer_email) customerEmail.value = response.customer_email
    if (response.apple_pass_url) applePassUrl.value = response.apple_pass_url
    if (response.google_pass_url) googlePassUrl.value = response.google_pass_url
    if (response.create_individual_passes)
      createIndividualPasses.value = response.create_individual_passes
    if (response.items) items.value = response.items
    if (response.wallet) wallet.value = response.wallet
    if (response.customer_wallet_email) customerWalletEmail.value = response.customer_wallet_email
    if (response.status) status.value = response.status
    if (response.refunds) refunds.value = response.refunds
  }

  return {
    id,
    customerId,
    customerEmail,
    customerName,
    customerPhone,
    customerAddressLine1,
    customerAddressLine2,
    customerCity,
    customerCountry,
    customerPostalCode,
    customerState,
    total,
    isProcessing,
    applePassUrl,
    totalInCents,
    googlePassUrl,
    paymentMethod,
    createIndividualPasses,
    referenceId,
    passesQuantity,
    hasPrimaryCard,
    items,
    customerWalletEmail,
    wallet,
    status,
    refunds,
    isLoaded,
    fetchOrder,
    updateFromResponse,
    fetchOrders,
    resendOrderEmail
  }
})
